import React from 'react';
import { CheckCircle, XCircle, AlertTriangle, Lock, Package, Users } from 'lucide-react';
import SEO from '../components/SEO';

const TipsRules = () => {
  const packingTips = [
    'Do not use plastic wrap on items',
    'Allow air flow between items',
    'Allow air flow between concrete and moisture sensitive belongings (moisture remains in concrete and will transfer)',
    'Rental agreement is available by request or online 24 hrs a day at www.vdstoragestreetman.com'
  ];

  const storageRules = [
    { icon: <XCircle className="h-6 w-6 text-red-600" />, text: 'NO FOOD', allowed: false },
    { icon: <XCircle className="h-6 w-6 text-red-600" />, text: 'NO SMOKING', allowed: false },
    { icon: <XCircle className="h-6 w-6 text-red-600" />, text: 'NO FLAMMABLES', allowed: false },
    { icon: <XCircle className="h-6 w-6 text-red-600" />, text: 'NO HAZARDOUS ITEMS', allowed: false },
    { icon: <CheckCircle className="h-6 w-6 text-green-600" />, text: 'TENANT INSURANCE IS REQUIRED', allowed: true },
    { icon: <CheckCircle className="h-6 w-6 text-green-600" />, text: 'PLEASE ENSURE THAT YOUR UNIT DOOR IS CLOSED AND LOCKED', allowed: true }
  ];

  const protectionTips = [
    {
      icon: <Lock className="h-8 w-8 text-green-600" />,
      title: 'Use Quality Locks',
      description: 'Invest in a high-quality disc or cylinder lock for maximum security. Avoid using combination locks.'
    },
    {
      icon: <Package className="h-8 w-8 text-green-600" />,
      title: 'Protect Your Items',
      description: 'Use covers and plastic sheeting to protect furniture and mattresses from dust and moisture.'
    },
    {
      icon: <Users className="h-8 w-8 text-green-600" />,
      title: 'Keep Records',
      description: 'Maintain an inventory of stored items and take photos for insurance purposes.'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title="Storage Tips & Facility Rules | V&D Storage Near Richland Chambers Lake"
        description="Packing tips and facility rules for V&D Storage on FM 416, near Richland Chambers Lake. Learn how to store your belongings safely and what items are not permitted."
        canonical="/tips-rules"
      />
      {/* Header */}
      <section className="bg-green-600 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4" data-testid="tips-rules-title">
            Storage Tips & Rules
          </h1>
          <p className="text-xl text-green-100">
            Make the most of your storage space and keep your items safe
          </p>
        </div>
      </section>

      {/* Packing Tips */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Packing Tips
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Follow these tips to maximize your storage space and protect your belongings
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {packingTips.map((tip, index) => (
              <div
                key={index}
                className="bg-gray-50 p-6 rounded-lg border border-gray-200 hover:border-green-300 transition-colors"
                data-testid={`packing-tip-${index}`}
              >
                <div className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
                  <p className="text-gray-700">{tip}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Storage Rules */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Facility Rules
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Please follow these guidelines to ensure a safe environment for everyone
            </p>
          </div>

          <div className="bg-white rounded-lg shadow-lg p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {storageRules.map((rule, index) => (
                <div
                  key={index}
                  className={`flex items-start p-4 rounded-lg ${rule.allowed ? 'bg-green-50' : 'bg-red-50'
                    }`}
                  data-testid={`storage-rule-${index}`}
                >
                  <div className="mr-3 mt-0.5 flex-shrink-0">{rule.icon}</div>
                  <p className="text-gray-700">{rule.text}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Protection Tips */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Protect Your Belongings
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Additional tips to keep your stored items secure and well-maintained
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {protectionTips.map((tip, index) => (
              <div
                key={index}
                className="bg-white p-8 rounded-xl shadow-md border border-gray-200 hover:border-green-300 transition-colors"
                data-testid={`protection-tip-${index}`}
              >
                <div className="mb-4">{tip.icon}</div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{tip.title}</h3>
                <p className="text-gray-600">{tip.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Important Notice */}
      <section className="py-16 bg-yellow-50 border-t border-b border-yellow-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-start">
            <AlertTriangle className="h-8 w-8 text-yellow-600 mr-4 mt-1 flex-shrink-0" />
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">Important Notice</h3>
              <p className="text-gray-700 mb-4">
                <strong>Actual unit sizes may vary from approximate size estimates.</strong> Units should be inspected prior to rental and any rental should be based on the inspection of the unit and not on the advertised unit size.
              </p>
              <p className="text-gray-700">
                Violations of facility rules may result in immediate termination of your rental agreement.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-green-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">Need Help or Have Questions?</h2>
          <p className="text-xl text-green-100 mb-8">
            Our friendly staff is here to help you with any questions about storage best practices.
          </p>
          <a
            href="/contact"
            className="inline-flex items-center px-8 py-3 text-lg font-semibold text-green-600 bg-white rounded-lg hover:bg-gray-100 transition-colors shadow-lg"
            data-testid="contact-cta-button"
          >
            Contact Us
          </a>
        </div>
      </section>
    </div>
  );
};

export default TipsRules;